import { ENTITY_TYPE, FLOW_TYPE, RENEW_PREFIX } from 'consts/flows';
import { PROCESS_STATUS } from 'consts/processStatus';
import { MIFID_STATUS } from 'consts/statuses';
import type { AppCtx } from 'providers/AppContext';
import { baseAction } from 'store/actions';
import type { EntityProcessInfo, ProcessInfo } from 'types';
import { createProcessFlowTypeNumber } from './converters';
import { isCompany } from './kennitala';

export function companyTranslationKey(flowType: string, companies: EntityProcessInfo[]) {
	return FLOW_TYPE.ES_MV !== flowType && companies.length ? 'CONNECTED_TO_COMPANIES' : '';
}

export function hasNotFinishedMifid(mifidStatus: number, flowType: string) {
	return MIFID_STATUS.HasNotFinishedMifid === mifidStatus && FLOW_TYPE.AV !== flowType;
}

export function selectItem(ssn: string, ctx: AppCtx) {
	const { children, companies, person } = ctx.getRelations();
	const context = [...children, ...companies, person].find(x => ssn === x.kennitala && !x.disabledCard);
	context && ctx.dispatch([baseAction, { context }]);
}

export function autoSelect(ctx: AppCtx, onSelect: (kt: string) => void) {
	const { config, state } = ctx;
	const { companyKennitala, isManualSignCompany } = state.user;
	config.IS_EMPLOYEE_WEB && isManualSignCompany && companyKennitala && onSelect(companyKennitala);
}

export function autoOnboard(ctx: AppCtx) {
	const { config, flowType, getRelations, state } = ctx;
	const { companies, person } = getRelations();
	if (
		state.context.kennitala &&
		!config.IS_EMPLOYEE_WEB &&
		FLOW_TYPE.ES_MV !== flowType &&
		!companies.length &&
		!person.disabledCard
	) {
		onboard(ctx);
	}
}

export function isProcessStarted(flowType: string, processStatus: number) {
	return (
		FLOW_TYPE.ES_MV === flowType &&
		processStatus >= PROCESS_STATUS.Started &&
		!(
			PROCESS_STATUS.InSigning === processStatus ||
			PROCESS_STATUS.InProcessing === processStatus ||
			PROCESS_STATUS.Finished === processStatus
		)
	);
}

export function isCancelProcessAvailable(
	IS_EMPLOYEE_WEB: boolean,
	entity: EntityProcessInfo,
	flowType: string
) {
	return (
		IS_EMPLOYEE_WEB &&
		entity.processInfo &&
		(FLOW_TYPE.AV === flowType ||
			FLOW_TYPE.EB === flowType ||
			FLOW_TYPE.ER === flowType ||
			FLOW_TYPE.MV === flowType)
	);
}

export async function handleSubmit(ctx: AppCtx, setModal: (x: boolean) => void) {
	const { config, flowType, http, state } = ctx;
	const { kennitala } = state.context;
	if (kennitala) {
		if (FLOW_TYPE.AV === flowType) {
			const res = await http.get<{ mifidStatus: number }>(`${config.API.getMifidStatus}?ssn=${kennitala}`);
			MIFID_STATUS.HasNotFinishedMifid === res?.mifidStatus ? setModal(true) : onboard(ctx);
		} else {
			onboard(ctx);
		}
	}
}

async function onboard(ctx: AppCtx) {
	const { fetchRelations, flowType, state } = ctx;
	const { context } = state;
	if (isNewProcessRequired(context.processInfo, flowType)) {
		await startNewProcess(ctx);
	}
	await fetchRelations(true, getEntityType(context.kennitala));
}

async function startNewProcess(ctx: AppCtx) {
	const { config, dispatch, http, state, t } = ctx;
	const payload = processPayload(ctx);
	const msg = t('creating_process_label', 'userboard');
	const processInfo = await http.post(config.API.createAgreementProcess, payload, msg);
	dispatch([baseAction, { context: { ...state.context, processInfo } }]);
}

function getEntityType(ssn: string) {
	return isCompany(ssn) ? ENTITY_TYPE.LEGAL_ENTITY : ENTITY_TYPE.INDIVIDUAL;
}

function isNewProcessRequired(process: ProcessInfo | null, flowType: string = '') {
	return (
		!process ||
		(process &&
			process.processStatus >= PROCESS_STATUS.Finished &&
			(flowType.startsWith(RENEW_PREFIX) || FLOW_TYPE.ES_MV === flowType))
	);
}

function processPayload(ctx: AppCtx) {
	const { config, flowType, state } = ctx;
	const { email, isManualSignCompany, kennitala, phone } = state.user;
	const { kennitala: selected } = state.context;
	return config.IS_EMPLOYEE_WEB
		? {
				email,
				flowType: createProcessFlowTypeNumber(flowType),
				initiatorKennitala: kennitala,
				isManualSignCompany: isManualSignCompany,
				phoneNumber: phone,
				processKennitala: selected
			}
		: {
				flowType: createProcessFlowTypeNumber(flowType),
				kennitala: selected
			};
}
